import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/client';
import { financialYearsKey, type FinancialYear } from '@/api/masters';
import {
  subscriptionNotice,
  type SubscriptionNotice as Notice,
} from '@core/session/subscriptionNotice';
import { can } from '@core/session/capability';
import type { AccessibleBranch, Session } from '@core/session/types';
import { closeOpenDropdowns } from './preline';

/**
 * Company · Branch · Financial year.
 *
 * The template's `company-dropdown` — the same pill, the same dark icon box, the same ellipsis —
 * carrying three values instead of one. That difference is the point: Dreams ERP's version shows a
 * company name and nothing else, because a template has nothing to scope. Every document in this
 * system is scoped to a *branch* and a *financial year*, and posting a challan against the wrong
 * shop or the wrong year is expensive to unpick.
 *
 * So the pill shows the branch, since that is the one an operator changes and the one that decides
 * what the screen behind it is listing, with the financial year beside it in the single warm accent
 * this product allows itself. The company is a fact that never changes within a session, so it
 * moves inside the dropdown where it costs no width. An earlier version laid all three out as
 * labelled fields across the topbar; it was legible and it was three times as wide as the
 * template's, which is what made the chrome look like a different product.
 */
export function ContextStrip({
  session,
  onBranchChange,
}: {
  session: Session;
  onBranchChange: (branch: AccessibleBranch) => void;
}) {
  const { user, branch } = session;
  const multiBranch = user.branches.length > 1;

  // The year new documents default to, read from the server rather than assumed. Gated on the
  // permission because this strip is on every screen for every user, and a counter clerk without
  // FinancialYear:View would otherwise generate a 403 on every page load.
  const currentYear = useQuery({
    queryKey: [...financialYearsKey, 'current'],
    queryFn: () => apiFetch<FinancialYear>('/api/financial-year/current'),
    // It changes once a year. Refetching it on every window focus is noise.
    enabled: can(session, 'FinancialYear:View'),
    staleTime: 60 * 60_000,
    retry: false,
  });

  // An em dash rather than a guess: a company with no current year set is a real state, and showing
  // last year's code because it happens to be hardcoded is how a document gets posted into the
  // wrong one.
  const yearCode = currentYear.data?.code ?? '—';

  return (
    /*
      Visible at every width, deliberately.

      This carried `hidden lg:flex` and that was a real defect rather than a tidy-up: below 1024px
      the pill disappeared with nothing behind it — not in the drawer, not in the account menu — so
      a phone or a portrait tablet could neither see which branch it was acting at nor change it.
      Every list in the product is branch-scoped and `X-Branch-Id` comes from here, which made a
      tablet user silently stuck on whatever branch was last chosen at a desk.

      It fits because the two disabled placeholder buttons beside it now stand down on small screens
      (see Topbar) — a control that does nothing should not outrank the one that decides what the
      whole screen is listing.
    */
    <div className="header-item company-dropdown hs-dropdown relative me-auto flex [--auto-close:inside] [--placement:bottom-left]">
      <div
        className="hs-dropdown-toggle flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border-color bg-white px-2 py-[6px]"
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded="false"
        aria-label="Company, branch and financial year"
      >
        <div className="flex items-center gap-2">
          <span className="flex size-5 items-center justify-center rounded-md bg-dark">
            <i className="icon icon-building-2 text-[11px] text-white" />
          </span>

          <span className="font-mono-nums text-[13px] font-semibold text-gray-900">
            {branch?.code ?? user.tenantCode}
          </span>

          <span className="h-3.5 w-px bg-border-color" />

          {/* The financial year keeps the only warm accent in the product; nothing else competes
              for it, which is what makes "wrong year" catch the eye at a glance. */}
          <span className="font-mono-nums text-[13px] font-semibold text-warning">{yearCode}</span>
        </div>

        <i className="icon icon-ellipsis text-default" />
      </div>

      <div
        className="hs-dropdown-menu duration z-1 mt-0 hidden min-w-64 space-y-1 rounded-md border border-border-color bg-white p-3 opacity-0 shadow transition-[opacity,margin] hs-dropdown-open:opacity-100"
        role="menu"
        aria-orientation="vertical"
      >
        <div className="border-b border-border-color pb-2">
          <p className="text-[11px] tracking-wide text-default uppercase">Company</p>
          {/* The code, not the trading name: `/api/auth/me` returns only the code, and the name is
              known just to the sign-in screen, which resolved it before authenticating. Reaching
              for it here would mean a second source of truth for which company you are in. */}
          <p className="font-mono-nums text-sm font-semibold text-gray-900">{user.tenantCode}</p>
        </div>

        <div className="py-1">
          <p className="mb-1 text-[11px] tracking-wide text-default uppercase">
            {multiBranch ? 'Switch branch' : 'Branch'}
          </p>

          {multiBranch ? (
            user.branches.map((option) => {
              const selected = option.id === branch?.id;

              return (
                <button
                  key={option.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-light ${
                    selected ? 'bg-primary-transparent text-primary' : 'text-gray-900'
                  }`}
                  onClick={() => {
                    onBranchChange(option);

                    // The shell does not unmount on a branch change — the menu that was opened to
                    // make this choice is still in the DOM, and still open, once it is made.
                    closeOpenDropdowns();
                  }}
                >
                  <span className="font-mono-nums text-[13px] font-semibold">{option.code}</span>
                  <span className="truncate text-default">{option.name}</span>
                  {selected && <i className="icon icon-check ms-auto text-[13px]" />}
                </button>
              );
            })
          ) : (
            <p className="px-2 py-1 text-sm text-gray-900">
              <span className="font-mono-nums font-semibold">{branch?.code ?? '—'}</span>{' '}
              <span className="text-default">{branch?.name}</span>
            </p>
          )}
        </div>

        <div className="border-t border-border-color pt-2">
          <p className="text-[11px] tracking-wide text-default uppercase">Financial year</p>
          <p className="font-mono-nums text-sm font-semibold text-warning">{yearCode}</p>
          {/* Read-only on purpose. The current year is a company-level setting changed on the
              Financial years screen, where closing and reopening a year lives with it — making it
              switchable from the topbar would let someone post into last year by accident. */}
          <p className="mt-0.5 text-[12px] text-default">
            New documents are numbered into this year.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The subscription banner, driven by the entitlements `/api/auth/me` returns.
 *
 * Read-only is warned about rather than errored: the customer has not done anything wrong, they
 * have an invoice to settle, and colouring it red teaches people to read the app as broken.
 */
export function SubscriptionNotice({ session }: { session: Session }) {
  // The rule is the shared core's, so the phone can say the same thing the same way; the dates go
  // through `formatDate` there, never a hardcoded locale that would disagree with every other date.
  const notice = subscriptionNotice(session.user.entitlements, session.user.isAdministrator);

  if (!notice) {
    return null;
  }

  /*
    The short form is for widths the sentence does not fit. It used to be `hidden md:inline-block`,
    which meant a read-only tenant on a phone got no warning at all and then hit save failures with
    no explanation — the one state where saying nothing is worse than saying it briefly.
  */
  return (
    <span className={`me-2 shrink-0 rounded px-2 py-0.5 text-[11px] ${noticeTones[notice.tone]}`}>
      <span className="sm:hidden">{notice.short}</span>
      <span className="max-sm:hidden">{notice.message}</span>
    </span>
  );
}

/** Written out, because Tailwind scans source text and an interpolated class never appears. */
const noticeTones: Record<Notice['tone'], string> = {
  neutral: 'bg-light text-default',
  warning: 'bg-warning-transparent text-warning',
  negative: 'bg-danger-transparent text-danger',
};
